import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    InstitutionItemTemplateCategoryBreadcrumbs,
    InstitutionItemTemplateCategoryCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsInstitutionsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionItemTemplateCategoriesActions from "../../../actions/institutionItemTemplateCategories";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { InstitutionLookupsLayout } from "../../index";

import "./InstitutionItemTemplateCategoryPage.scss";

interface InstitutionItemTemplateCategoryPageProps {
    form: Dtos.InstitutionItemTemplateCategory & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    institutionItemTemplateCategory: Dtos.InstitutionItemTemplateCategory;
    lookups: Dtos.Lookup[];
    loadingInstitutionItemTemplateCategory: boolean;
    loadInstitutionItemTemplateCategoryFailure: boolean;
    loadInstitutionItemTemplateCategoryErrorCode: string;
    savingInstitutionItemTemplateCategory: boolean;
    saveInstitutionItemTemplateCategorySuccess: boolean;
    saveInstitutionItemTemplateCategoryFailure: boolean;
    creatingInstitutionItemTemplateCategory: boolean;
    createInstitutionItemTemplateCategorySuccess: boolean;
    validatingInstitutionItemTemplateCategory: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearInstitutionItemTemplateCategory: InstitutionItemTemplateCategoriesActions.IInstitutionItemTemplateCategoryClearActionCreator;
    loadInstitutionItemTemplateCategory: InstitutionItemTemplateCategoriesActions.IInstitutionItemTemplateCategoryLoadByIdActionCreator;
    saveInstitutionItemTemplateCategory: InstitutionItemTemplateCategoriesActions.IInstitutionItemTemplateCategorySaveActionCreator;
    createInstitutionItemTemplateCategory: InstitutionItemTemplateCategoriesActions.IInstitutionItemTemplateCategoryCreateActionCreator;
    validateInstitutionItemTemplateCategory: InstitutionItemTemplateCategoriesActions.IInstitutionItemTemplateCategoryValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.institutionItemTemplateCategory";

const InstitutionItemTemplateCategoryPage: FC<
    InstitutionItemTemplateCategoryPageProps
> = ({
    form,
    formState,
    formProperties,
    institutionItemTemplateCategory,
    lookups,
    loadingInstitutionItemTemplateCategory,
    loadInstitutionItemTemplateCategoryFailure,
    loadInstitutionItemTemplateCategoryErrorCode,
    savingInstitutionItemTemplateCategory,
    saveInstitutionItemTemplateCategorySuccess,
    saveInstitutionItemTemplateCategoryFailure,
    creatingInstitutionItemTemplateCategory,
    createInstitutionItemTemplateCategorySuccess,
    validatingInstitutionItemTemplateCategory,
    validationFailures,
    permissions,
    clearInstitutionItemTemplateCategory,
    loadInstitutionItemTemplateCategory,
    saveInstitutionItemTemplateCategory,
    createInstitutionItemTemplateCategory,
    validateInstitutionItemTemplateCategory,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { institutionItemTemplateCategoryId } = useParams<{
        institutionItemTemplateCategoryId: string;
    }>();
    const institutionItemTemplateCategoryIdNumber = parseInt(
        institutionItemTemplateCategoryId,
        10
    );

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.Category = {} as Dtos.Category;

        loadForm(
            reduxFormName,
            Object.assign({ ...institutionItemTemplateCategory }, extraData)
        );
    };


    // Load institution item template category
    useEffect(() => {
        if (
            institutionItemTemplateCategoryIdNumber &&
            !institutionItemTemplateCategory &&
            !loadingInstitutionItemTemplateCategory
        ) {
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(
                InstitutionItemTemplateCategoryBreadcrumbs(
                    institutionItemTemplateCategoryIdNumber
                )
            );
            loadInstitutionItemTemplateCategory(
                institutionItemTemplateCategoryIdNumber
            );
        } else if (
            !institutionItemTemplateCategoryIdNumber &&
            !creatingInstitutionItemTemplateCategory &&
            !createInstitutionItemTemplateCategorySuccess
        ) {
            createInstitutionItemTemplateCategory();
            setBreadcrumbs(InstitutionItemTemplateCategoryCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (institutionItemTemplateCategory && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        institutionItemTemplateCategoryIdNumber,
        institutionItemTemplateCategory,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        permissions,
        setBreadcrumbs,
        InstitutionItemTemplateCategoryBreadcrumbs,
        loadInstitutionItemTemplateCategory,
        createInstitutionItemTemplateCategory,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingInstitutionItemTemplateCategory,
        creatingInstitutionItemTemplateCategory,
        createInstitutionItemTemplateCategorySuccess,
        InstitutionItemTemplateCategoryCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionItemTemplateCategoryFailure) {
            navigate(`/error/${loadInstitutionItemTemplateCategoryErrorCode}`);
        }
    }, [
        loadInstitutionItemTemplateCategoryFailure,
        loadInstitutionItemTemplateCategoryErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            institutionItemTemplateCategoryIdNumber &&
            institutionItemTemplateCategory?.value
        ) {
            setBreadcrumbs(
                InstitutionItemTemplateCategoryBreadcrumbs(
                    institutionItemTemplateCategoryIdNumber,
                    institutionItemTemplateCategory?.value
                )
            );
        }
    }, [
        institutionItemTemplateCategoryIdNumber,
        institutionItemTemplateCategory,
        setBreadcrumbs,
        InstitutionItemTemplateCategoryBreadcrumbs,
    ]);

    // Reload institution item template category after saving
    useEffect(() => {
        if (
            !savingInstitutionItemTemplateCategory &&
            saveInstitutionItemTemplateCategorySuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={
                        <span>
                            Institution item template category saved successfully
                        </span>
                    }
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string =
                    "/settings/institution/itemtemplatecategories";
                navigate(url);
            } else if (!institutionItemTemplateCategoryIdNumber) {
                let url: string = `/settings/institution/itemtemplatecategories/${institutionItemTemplateCategory?.id}`;
                navigate(url);
                clearInstitutionItemTemplateCategory();
            }
        }
    }, [
        savingInstitutionItemTemplateCategory,
        saveInstitutionItemTemplateCategorySuccess,
        institutionItemTemplateCategoryIdNumber,
        navigate,
        clearInstitutionItemTemplateCategory,
        saveAndReturn,
        institutionItemTemplateCategory,
    ]);

    // Failed saving
    useEffect(() => {
        if (
            !savingInstitutionItemTemplateCategory &&
            saveInstitutionItemTemplateCategoryFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingInstitutionItemTemplateCategory,
        saveInstitutionItemTemplateCategoryFailure,
    ]);

    // Cleanup effect to clear the institution item template category on component unmount
    useEffect(() => {
        return () => {
            clearInstitutionItemTemplateCategory();
            resetForm(reduxFormName);
        };
    }, [clearInstitutionItemTemplateCategory, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/institution/itemtemplatecategories";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.Category) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.Category = {} as Dtos.Category;

            await saveInstitutionItemTemplateCategory(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.Category = {} as Dtos.Category;

            if (
                form &&
                hasBeenSubmitted &&
                !validatingInstitutionItemTemplateCategory
            ) {
                await validateInstitutionItemTemplateCategory(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.CategoriesUpdate,
                Dtos.Permission.CategoriesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingInstitutionItemTemplateCategory ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "institutionItemTemplateCategory",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "institutionItemTemplateCategory",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "institutionItemTemplateCategory",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "institutionItemTemplateCategory",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingInstitutionItemTemplateCategory,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <InstitutionLookupsLayout
            loading={loadingInstitutionItemTemplateCategory}
            permission={Dtos.Permission.CategoriesView}
        >
            {renderContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionItemTemplateCategoryPageProps> => {
    let institutionItemTemplateCategory:
        | Dtos.InstitutionItemTemplateCategory
        | undefined = !(
        state.institutionItemTemplateCategories.formData instanceof Array
    )
        ? state.institutionItemTemplateCategories.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institutionItemTemplateCategory: institutionItemTemplateCategory!,
        loadingInstitutionItemTemplateCategory:
            state.institutionItemTemplateCategories.loadState &&
            state.institutionItemTemplateCategories.loadState.status ==
                RequestState.Pending,
        loadInstitutionItemTemplateCategoryFailure:
            state.institutionItemTemplateCategories.loadState &&
            state.institutionItemTemplateCategories.loadState.status ==
                RequestState.Failure,
        loadInstitutionItemTemplateCategoryErrorCode:
            state.institutionItemTemplateCategories.loadState.errorCode,
        savingInstitutionItemTemplateCategory:
            state.institutionItemTemplateCategories.saveState &&
            state.institutionItemTemplateCategories.saveState.status ==
                RequestState.Pending,
        saveInstitutionItemTemplateCategorySuccess:
            state.institutionItemTemplateCategories.saveState &&
            state.institutionItemTemplateCategories.saveState.status ==
                RequestState.Success,
        saveInstitutionItemTemplateCategoryFailure:
            state.institutionItemTemplateCategories.saveState &&
            state.institutionItemTemplateCategories.saveState.status ==
                RequestState.Failure,
        creatingInstitutionItemTemplateCategory:
            state.institutionItemTemplateCategories.createState &&
            state.institutionItemTemplateCategories.createState.status ==
                RequestState.Pending,
        createInstitutionItemTemplateCategorySuccess:
            state.institutionItemTemplateCategories.createState &&
            state.institutionItemTemplateCategories.createState.status ==
                RequestState.Success,
        validatingInstitutionItemTemplateCategory:
            state.institutionItemTemplateCategories.validateState &&
            state.institutionItemTemplateCategories.validateState.status ==
                RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.institutionItemTemplateCategory,
        formState: state.institutionItemTemplateCategories.formState,
        formProperties: state.institutionItemTemplateCategories.formProperties,
        validationFailures: institutionItemTemplateCategory
            ? state.institutionItemTemplateCategories.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<InstitutionItemTemplateCategoryPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadInstitutionItemTemplateCategory: bindActionCreators(
            InstitutionItemTemplateCategoriesActions.LoadInstitutionItemTemplateCategoryById,
            dispatch
        ),
        clearInstitutionItemTemplateCategory: bindActionCreators(
            InstitutionItemTemplateCategoriesActions.Clear,
            dispatch
        ),
        saveInstitutionItemTemplateCategory: bindActionCreators(
            InstitutionItemTemplateCategoriesActions.SaveInstitutionItemTemplateCategory,
            dispatch
        ),
        createInstitutionItemTemplateCategory: bindActionCreators(
            InstitutionItemTemplateCategoriesActions.CreateInstitutionItemTemplateCategory,
            dispatch
        ),
        validateInstitutionItemTemplateCategory: bindActionCreators(
            InstitutionItemTemplateCategoriesActions.ValidateInstitutionItemTemplateCategory,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionItemTemplateCategoryPage);
